import React, {
  useState, useContext,
} from 'react';
import './settings.css';
import Checkbox from '@mui/material/Checkbox';
import { Button } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import { sendRequest } from '../../api/shootAPI';

function Authentication() {
  const {
    activeWorkspace,
    setActiveWorkspace,
  } = useContext(UserContext);
  const {
    displayNotification,
  } = useContext(NotificationContext);
  const [
    checked, setChecked,
  ] = useState(activeWorkspace.authentication && activeWorkspace.authentication !== 'None');
  const [authentication, setAuthentication] = useState(activeWorkspace.authentication);

  // SAML configuration state
  const [samlConfig, setSamlConfig] = useState({
    samlEntryPoint: activeWorkspace.samlEntryPoint || '',
    samlIssuer: activeWorkspace.samlIssuer || '',
    samlCert: activeWorkspace.samlCert || '',
  });

  const [samlErrors, setSamlErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sendAuthenticationRequest = async (data) => {
    const res = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.authentication(activeWorkspace.id),
      data,
    });
    if (res.success) {
      displayNotification({
        message: 'Authentication has been set up successfully.',
        severity: 'success',
      });
      return true;
    }
    displayNotification({
      message: 'An error occurs while setting up authentication. Please try again.',
      severity: 'error',
    });

    return false;
  };
  const handleChange = (event) => {
    const isChecked = event.target.checked;
    // eslint-disable-next-line no-nested-ternary
    const newAuthentication = isChecked ? (authentication !== 'None' ? authentication : 'Email') : 'None';

    setChecked(isChecked);
    setAuthentication(newAuthentication);
    setHasUnsavedChanges(true);
  };
  const handleSelectChange = (event) => {
    const newAuthentication = event.target.value;
    setAuthentication(newAuthentication);
    setHasUnsavedChanges(true);
  };

  const handleSamlConfigChange = (field, value) => {
    setSamlConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  const validateSamlConfig = () => {
    const errors = {};
    if (!samlConfig.samlEntryPoint) {
      errors.samlEntryPoint = 'Entry Point URL is required';
    } else if (!/^https?:\/\/.+/.test(samlConfig.samlEntryPoint)) {
      errors.samlEntryPoint = 'Must be a valid URL starting with http:// or https://';
    }

    if (!samlConfig.samlIssuer) {
      errors.samlIssuer = 'Issuer/Entity ID is required';
    }

    if (!samlConfig.samlCert) {
      errors.samlCert = 'X.509 Certificate is required';
    } else if (!samlConfig.samlCert.includes('BEGIN CERTIFICATE')
      || !samlConfig.samlCert.includes('END CERTIFICATE')) {
      errors.samlCert = 'Certificate must include BEGIN CERTIFICATE and END CERTIFICATE headers';
    }

    return errors;
  };

  const handleSaveChanges = async () => {
    // Reset errors
    setSamlErrors({});

    let data = {
      authentication,
    };

    // If SAML is selected, validate and include SAML configuration
    if (authentication === 'SAML') {
      const errors = validateSamlConfig();
      if (Object.keys(errors).length > 0) {
        setSamlErrors(errors);
        displayNotification({
          message: 'Please fix the SAML configuration errors before saving.',
          severity: 'error',
        });
        return;
      }

      data = {
        ...data,
        ...samlConfig,
      };
    }

    const res = await sendAuthenticationRequest(data);
    if (res) {
      setActiveWorkspace({
        ...activeWorkspace,
        authentication,
        ...(authentication === 'SAML' ? samlConfig : {}),
      });
      setSamlErrors({});
      setHasUnsavedChanges(false);
    }
  };

  const handleDiscardChanges = () => {
    // Reset to original values
    const originalAuth = activeWorkspace.authentication || 'None';
    setAuthentication(originalAuth);
    setChecked(originalAuth !== 'None');
    setSamlConfig({
      samlEntryPoint: activeWorkspace.samlEntryPoint || '',
      samlIssuer: activeWorkspace.samlIssuer || '',
      samlCert: activeWorkspace.samlCert || '',
    });
    setSamlErrors({});
    setHasUnsavedChanges(false);
  };

  return (
    <div className="d-flex flex-column">
      <h5 className="fw-semibold">Authentication</h5>
      <FormControlLabel
        label={(
          <p className="fw-normal">
            {`Require two factor authentication (via 
                ${authentication !== 'None' ? authentication.toLowerCase() : 'email'})
                 for admins`}
          </p>
        )}
        control={(
          <Checkbox
            checked={checked}
            onChange={handleChange}
            sx={{
              color: 'var(--blue-3)',
              '&.Mui-checked': {
                color: 'var(--blue-3)',
              },
            }}
          />
        )}
      />
      {
        checked
          && (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center two-factor-row">
                <p className="mr-3">Two factor method:</p>
                <FormControl sx={{ minWidth: 250 }} size="small">
                  <Select
                    value={authentication}
                    onChange={handleSelectChange}
                    input={<OutlinedInput />}
                  >
                    <MenuItem value="Email">
                      Email (default)
                    </MenuItem>
                    <MenuItem value="Google Authenticator">
                      Google Authenticator
                    </MenuItem>
                    <MenuItem value="SAML">
                      SAML SSO
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
              {authentication === 'Google Authenticator' && (
                <div className="mt-2">
                  <p className="text-muted small mb-1">
                    <strong>Note:</strong>
                    {' '}
                    Users will need to set up an authenticator app (like Google Authenticator,
                    Authy, or Microsoft Authenticator) on their mobile device.
                  </p>
                  <p className="text-muted small">
                    Each user will see a QR code during their next login to complete the setup.
                  </p>
                </div>
              )}
              {authentication === 'SAML' && (
                <div className="mt-3">
                  <p className="text-muted small mb-2">
                    <strong>SAML SSO Configuration:</strong>
                    {' '}
                    Configure your SAML identity provider settings below.
                  </p>
                  <div className="saml-config-form">
                    <div className="mb-3">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="samlEntryPoint" className="form-label small text-muted">
                        SSO Entry Point URL *
                      </label>
                      <input
                        type="url"
                        className={`form-control form-control-sm ${samlErrors.samlEntryPoint ? 'is-invalid' : ''}`}
                        id="samlEntryPoint"
                        placeholder="https://your-idp.com/sso/saml"
                        value={samlConfig.samlEntryPoint}
                        onChange={(e) => handleSamlConfigChange('samlEntryPoint', e.target.value)}
                      />
                      {samlErrors.samlEntryPoint && (
                        <div className="invalid-feedback">{samlErrors.samlEntryPoint}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="samlIssuer" className="form-label small text-muted">
                        Issuer/Entity ID *
                      </label>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${samlErrors.samlIssuer ? 'is-invalid' : ''}`}
                        id="samlIssuer"
                        placeholder="your-workspace-identifier"
                        value={samlConfig.samlIssuer}
                        onChange={(e) => handleSamlConfigChange('samlIssuer', e.target.value)}
                      />
                      {samlErrors.samlIssuer && (
                        <div className="invalid-feedback">{samlErrors.samlIssuer}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="samlCert" className="form-label small text-muted">
                        X.509 Certificate *
                      </label>
                      <textarea
                        className={`form-control form-control-sm ${samlErrors.samlCert ? 'is-invalid' : ''}`}
                        id="samlCert"
                        rows="4"
                        placeholder="-----BEGIN CERTIFICATE-----
MIICXjCCAcegAwIBAgIBADANBgkqhkiG9w0BAQ...
-----END CERTIFICATE-----"
                        value={samlConfig.samlCert}
                        onChange={(e) => handleSamlConfigChange('samlCert', e.target.value)}
                      />
                      {samlErrors.samlCert && (
                        <div className="invalid-feedback">{samlErrors.samlCert}</div>
                      )}
                    </div>
                    <div className="alert alert-info small">
                      <strong>Service Provider Details:</strong>
                      <br />
                      • Metadata URL:
                      {' '}
                      {window.location.origin}
                      /api/auth/saml/metadata/
                      {activeWorkspace.id}
                      <br />
                      • ACS URL:
                      {' '}
                      {window.location.origin}
                      /api/auth/saml/callback/
                      {activeWorkspace.id}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        }
      {hasUnsavedChanges && (
      <div className="holidayForm-btns" style={{ marginTop: 24 }}>
        <Button
          onClick={handleDiscardChanges}
          color="secondary"
          sx={{
            marginRight: 2,
            textTransform: 'capitalize',
            border: 'inset',
            borderWidth: 1,
            borderColor: '#7D8698',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: 'capitalize' }}
          onClick={handleSaveChanges}
        >
          Save Changes
        </Button>
      </div>
      )}
    </div>
  );
}

export default Authentication;
